<template>
  <ul
    v-if="!$store.getters.isAuthorized"
    class="header-buttons"
    style="padding-right: 15px;"
    role="region"
    aria-label="profile menu button"
  >
    <li>
      <router-link
        to="/login"
        tag="button"
        class="btn btn-ghost"
      >
        Login
      </router-link>
    </li>
    <li>
      <router-link
        to="/register"
        tag="button"
        class="btn btn--primary"
      >
        Sign Up
      </router-link>
    </li>
  </ul>
  <div
    v-else
    class="user-menu"
  >
    <b-dropdown
      :id="id"
      right
      :style="isImpersonating ? `border: 2px solid red` : ''"
    >
      <template v-slot:button-content>
        <span
          role="region"
          aria-label="profile menu">
          <div class="left">
            <div
              v-if="$store.getters.currentUser && $store.getters.currentUser.photo && $store.getters.currentUser.photo.url"
              class="user-avatar"
              :style="{ backgroundImage: `url(${$store.getters.currentUser.photo.url})` }"
            />
            <div
              v-else
              class="user-avatar"
            />
            <div v-if="$store.getters.currentUser.num_pending_payments" class="indicator" />
          </div>
          <div class="right">
            <span class="name">
              {{ $store.getters.currentUser.fullNameShort }}
            </span>

            <span v-if="$store.getters.currentUser.num_pending_payments" class="message active payment-due">
              {{ $store.getters.currentUser.num_pending_payments }}
              <span v-if="$store.getters.currentUser.num_pending_payments === 1">Payment Due</span>
              <span v-else>Payments Due</span>
            </span>

            <span v-else-if="$globals.unreadMessages" class="message active">
              {{ $globals.unreadMessages }} New
              <span v-if="$globals.unreadMessages === 1">Message</span>
              <span v-else>Messages</span>
            </span>

            <span v-else class="message">
              {{ $store.getters.currentUser.userTypeDisplay }}
            </span>
          </div>
        </span>
      </template>
      <b-dropdown-item v-if="!$store.getters.currentUser.isCollegeAdmin" to="/inbox">
        <font-awesome-icon
          :icon="['fas', 'inbox']"
          class="icon alt"
        />
        Messages
        <div v-if="$globals.unreadMessages" class="count">
          {{ $globals.unreadMessages }}
        </div>
      </b-dropdown-item>
      <b-dropdown-item v-if="$store.getters.currentUser.isStudent" to="/payments">
        <font-awesome-icon
          :icon="['fas', 'credit-card']"
          class="icon alt"
        />
        Payments
        <div v-if="$store.getters.currentUser.num_pending_payments" class="count red">
          {{ $store.getters.currentUser.num_pending_payments }}
        </div>
      </b-dropdown-item>
      <b-dropdown-item :to="getAccountPath()">
        <font-awesome-icon
          :icon="['fas', 'user']"
          class="icon alt"
        />
        Account
      </b-dropdown-item>
      <b-dropdown-item v-if="isImpersonating" @click="stopImpersonate">
        <font-awesome-icon
          :icon="['fas', 'user-secret']"
          class="icon alt"
        />
        Stop Impersonate
      </b-dropdown-item>
      <b-dropdown-item v-if="!isImpersonating" @click="logout">
        <font-awesome-icon
          :icon="['fas', 'sign-out-alt']"
          class="icon alt"
        />
        Log Out
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
import UserClient from '@/api/UserClient';
import NavigationMenu from '@/components/NavigationMenu.vue'
import Vue from "vue";

export default {
  name: 'HeaderButtons',

  components: {
    NavigationMenu,
  },

  props: {
    id: {
      type: String,
      default: 'user-menu'
    }
  },

  data() {
    return {
      saving: false,
      userClient: new UserClient(this.$http),
      isImpersonating: !!Vue.prototype.$localStorage.get('impersonateId')
    };
  },

  methods: {
    impersonate() {
      Vue.prototype.$localStorage.set('impersonateId', prompt('User ID?'));
      window.location.assign('/');
    },

    stopImpersonate() {
      Vue.prototype.$localStorage.remove('impersonateId');
      window.location.assign('/');
    },

    logout() {
      this.$globals.keycloak.logout({ redirectUri: window.location.origin });
    },
    getAccountPath() {
      return this.$store.getters.currentUser.isCollegeAdmin ? '/my-account' : '/account';
    }
  },
};
</script>

<style lang="scss" scoped>
$header-height: 46px;

ul.header-buttons {
  margin-bottom: 0;
  list-style-type: none;
  padding-top: 8px;

  li {
    display: inline-block;
    line-height: calc(#{$header-height} - (#{$header-padding} * 2));
    vertical-align: top;

    &:first-of-type {
      margin-right: 5px;
    }

    button {
      position: relative;
      min-width: 84px;
      height: 30px;
      font-size: 15px;
      line-height: 1;
      border-radius: 20px;
    }
  }
}

.user-menu {
  ::v-deep .dropdown {
    padding: 0;
    margin: 0 !important;
    height: $header-height;
    width: 100%;

    @media (min-width: $lg-min) {
      width: auto;
    }

    button {
      height: 46px;
      background: none;
      padding: 5px 40px 5px 20px;
      border: none !important;
      border-radius: 0;

      .left {
        width: 55px;
        display: inline-block;
        vertical-align: top;
        position: relative;

        .indicator {
          @include border-radius (10px);

          height: 10px;
          width: 10px;
          background: $red;
          position: absolute;
          top: 2px;
          left: 32px;
        }
      }

      .right {
        width: calc(100% - 55px);
        display: inline-block;
        vertical-align: top;
        text-align: left;

        span {
          display: block;
          white-space: nowrap;

          &.name {
            font-size: 14px;
            font-weight: 500;
            letter-spacing: -0.02rem;
          }

          &.message {
            font-size: 12px;
            color: #aacce3;
            letter-spacing: 0.01rem;
            font-weight: normal;
            line-height: 1.05;

            &.active {
              color: $green;
            }
          }

          span {
            display: inline;
          }
        }
      }

      .user-avatar {
        @include background-cover;
        @include border-radius(100px);

        background-image: url('~@/assets/img/default-user.jpg');
        height: 34px;
        width: 34px;
        margin-top: 1px;
        margin-right: 15px;
      }
    }

    .dropdown-toggle::after {
      position: relative;
      top: 7px;
    }

    .dropdown-menu {
      width: calc(100% - 4px);
      background: $dark-blue;
      left: auto !important;
      right: -5px !important;
      color: rgba(255, 255, 255, 1);
      padding-top: 0;
      padding-bottom: 0;
      margin: 2px;

      li {
        position: relative;
        background: none !important;

        &:hover {
          background: none;
        }

        &:last-child {
          border: 0;
          margin-bottom: 0;
        }

        a {
          color: #dadada;
          font-size: 15px;
          height: 46px;
          line-height: 46px;
          padding: 0 1.5rem;
          background: none !important;
          display: inline-flex;
          flex-direction: row-reverse;
          align-items: center;

          &:hover {
            background: none;
            color: $green;
          }

          i,
          svg {
            margin-left: 16px;
            width: 16px;
          }
        }

        .count {
          border-radius: 18px;
          height: 18px;
          font-size: 12px;
          font-weight: bold;
          position: absolute;
          left: 10px;
          top: 14px;
          background: $green;
          color: $dark-blue;
          padding-left: 6px;
          padding-right: 6px;
          line-height: 18px;
        }
      }
    }
  }

  .payment-due {
    color: lighten($red, 10%) !important;
  }
}
</style>
